import { FC, useEffect } from 'react';
import { useSnackbar, SnackbarKey, OptionsObject } from 'notistack';
import { useTranslation } from 'react-i18next';

import { ILayoutState } from 'core/redux/reducers/layout/type';
import { closeSnackbar, removeSnackbar } from 'core/redux/actions/lyaout/action';
import { useShallowEqualSelector, useActions } from 'core/redux/hooks';

import CloseIcon from './CloseIcon';
import { RouteCoreComponentPropsType } from 'core/components/layout/Routing';

export const generateSnackbarKey = (): SnackbarKey => new Date().getTime() + Math.random();

const DismissButton: FC<{ onClose: () => void }> = ({ onClose }) => (
  <button style={{ color: '#fff', cursor: 'pointer' }} onClick={onClose}>
    <CloseIcon />
  </button>
);

let displayed: SnackbarKey[] = [];

const actions = {
  closeSnackbar: (key: SnackbarKey) => closeSnackbar(key),
  removeSnackbar: (key: SnackbarKey) => removeSnackbar(key),
};

interface INotifierProps extends OptionsObject {
  DismissCustomButton?: FC<{ onClose: () => void }>;
}

const Notifier: FC<INotifierProps & RouteCoreComponentPropsType> = ({
  DismissCustomButton,
  match,
  history,
  location,
  children,
  staticContext,
  ...props
}) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const notifications = useShallowEqualSelector(
    ({ layout }: { layout: ILayoutState }) => layout.notifications || []
  );

  const listActions = useActions(actions);

  const storeDisplayed = (id: SnackbarKey): void => {
    displayed = [...displayed, id];
  };

  const removeDisplayed = (id: SnackbarKey): void => {
    displayed = [...displayed.filter((key) => id !== key)];
  };

  const { t } = useTranslation();

  useEffect(() => {
    notifications.forEach(({ key, message, options = {}, dismissed = false }) => {
      if (dismissed) {
        // dismiss snackbar using notistack
        closeSnackbar(key);
        return;
      }

      // do nothing if snackbar is already displayed
      if (displayed.includes(key)) return;

      // display snackbar using notistack
      enqueueSnackbar(t(message), {
        key,
        ...options,
        onExited: (event, myKey) => {
          // remove this snackbar from redux store
          listActions.removeSnackbar(myKey);
          removeDisplayed(myKey);
        },
        action: (key) => {
          return DismissCustomButton ? (
            <DismissCustomButton onClose={() => listActions.closeSnackbar(key)} />
          ) : (
            <DismissButton onClose={() => listActions.closeSnackbar(key)} />
          );
        },
        ...props,
      });

      // keep track of snackbars that we've displayed
      storeDisplayed(key);
    });
  }, [notifications]);

  return <></>;
};

export default Notifier;
